import { useCallback, useEffect, useState } from 'react';

/**
 * Install-to-home-screen, which works completely differently on the two
 * platforms these devices run.
 *
 * **Android / Chromium** fire a `beforeinstallprompt` event. Calling
 * `preventDefault()` on it suppresses the browser's own mini-infobar and hands
 * you the event to trigger later from your own button. The event can only be
 * used once.
 *
 * **iOS fires nothing.** There is no install API at all — the user must tap
 * Share → Add to Home Screen by hand. So the only thing the app can do is
 * detect that it is running in Safari rather than installed, and show the
 * instructions.
 */

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function detectStandalone(): boolean {
  if (window.matchMedia('(display-mode: standalone)').matches) return true;
  // iOS predates the display-mode media query and exposes this instead.
  return (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
}

function detectIos(): boolean {
  const ua = navigator.userAgent;
  if (/iphone|ipod/i.test(ua)) return true;
  // iPadOS 13+ reports itself as a Mac; touch points are what give it away.
  if (/ipad/i.test(ua)) return true;
  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
}

/**
 * Chrome and Firefox on iOS are WebKit wrappers and cannot install a PWA at
 * all — only Safari can. Showing them the Share-sheet instructions would be
 * telling them to do something impossible.
 */
function detectIosSafari(): boolean {
  if (!detectIos()) return false;
  const ua = navigator.userAgent;
  return !/CriOS|FxiOS|EdgiOS|OPiOS/i.test(ua);
}

export type InstallState =
  | { kind: 'installed' }
  | { kind: 'prompt'; install: () => Promise<boolean> }
  | { kind: 'ios-manual' }
  | { kind: 'unavailable' };

export function useInstallPrompt(): InstallState {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(detectStandalone);

  useEffect(() => {
    const onBeforeInstallPrompt = (event: Event): void => {
      event.preventDefault();
      setDeferred(event as BeforeInstallPromptEvent);
    };
    const onInstalled = (): void => {
      setInstalled(true);
      setDeferred(null);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onInstalled);

    // Launching from the home screen after install does not fire `appinstalled`,
    // so re-check when the display mode changes.
    const media = window.matchMedia('(display-mode: standalone)');
    const onDisplayModeChange = (): void => setInstalled(detectStandalone());
    media.addEventListener('change', onDisplayModeChange);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onInstalled);
      media.removeEventListener('change', onDisplayModeChange);
    };
  }, []);

  const install = useCallback(async (): Promise<boolean> => {
    if (!deferred) return false;
    await deferred.prompt();
    const { outcome } = await deferred.userChoice;
    // The event is single-use; a dismissed prompt cannot be re-shown until the
    // browser decides to fire a new one.
    setDeferred(null);
    return outcome === 'accepted';
  }, [deferred]);

  if (installed) return { kind: 'installed' };
  if (deferred) return { kind: 'prompt', install };
  if (detectIosSafari()) return { kind: 'ios-manual' };
  return { kind: 'unavailable' };
}
